import axios from 'axios';
import { authenticationHeaders } from '../../../../../../guided/subcomponents/utils';
import { makeLogger } from '../../../../../../../logger/logger';
import type {
  ProductDetails,
  ProductDetailsSingleRequest,
  ProductDetailsResults,
  ProductDetailsRequestBody,
  ProductBody,
  ProductDetailsBundleResponse,
  searcherName,
} from './productDetails.types';
import type { ProductDocumentsData } from '../../../../../../../api/model/response/documents/public/ProductDocuments';
import type { ProductRangesData } from '../../../../../../../api/model/response/documents/public/ProductsRanges';

export const getProcutDetails: ProductDetails = async (
  apiKey,
  token,
  apigeeAuth,
  endpoint,
  brand,
  country,
  language,
  documentsTerm,
  rangesTerm,
  toggleDropdown,
  limit = 5,
  pathToFile
) => {
    const cleanDocumentsTerm = documentsTerm.replace(/(<([^>]+)>)/gi, '');
    const bundledResponse: ProductDetailsResults = {};
    const { err } = makeLogger({
      pathToCurrentFile:
            pathToFile ??
            'components/result-page/new-result-cards/public/product-card/subcomponents/card-cta-bottom-section/uiaas-search-product-dropdown-button/uiaas-search-product-dropdown-button.tsx',
    });

    const authHeaders = await authenticationHeaders({
      apikey: apiKey,
      tokenUrl: token,
      apigeeBasicAuth: apigeeAuth,
    });

    const requestHeaders = {
      headers: {
        'content-type': 'application/json',
        ...authHeaders,
      },
    };

    const documentsResponse = handleSingleRequest(
      endpoint,
      brand,
      country,
      language,
      cleanDocumentsTerm,
      limit,
      requestHeaders,
      err,
      'documents'
    );

    const rangesResponse = handleSingleRequest(
      endpoint,
      brand,
      country,
      language,
      rangesTerm,
      limit,
      requestHeaders,
      err,
      'ranges'
    );

    await axios
      .all([documentsResponse, rangesResponse])
      .then(
        axios.spread((...responses) => {
            responses.forEach((response) => {
                if (response)
                  bundledResponse[response.requestName] =
                            response.responseData;
            });
        })
      )
      .catch((errors) => {
          errors.forEach((error: any) => {//eslint-disable-line @typescript-eslint/no-explicit-any
              err(
                'There was a problem executing the request for documents and ranges',
                error
              );
          });
      })
      .finally(() => {
          toggleDropdown();
      });

    return bundledResponse;
};

export const buildRequestDetails: ProductDetailsRequestBody = (
  brand,
  country,
  language,
  term,
  limit,
  target
) => {
    const searcher = `${brand}/${
      target === 'documents' ? 'popular' : 'products'
    }/${target}`;

    const bodyObject: ProductBody = {
      searcher: searcher,
      country: country,
      language: language,
      term: term,
      forceTerm: target === 'documents' ? false : true,
    };

    if (target === 'documents')
      bodyObject.pagination = {
        limit: limit,
        offset: 0,
      };

    return bodyObject;
};

export const handleSingleRequest: ProductDetailsSingleRequest = (
  endpoint,
  brand,
  country,
  language,
  term,
  limit,
  requestHeaders,
  logger,
  target
) => {
    if (term)
      return axios
        .post(
          endpoint,
          buildRequestDetails(brand, country, language, term, limit, target),
          requestHeaders
        )
        .then((response) => bundleResponse(response, logger, target))
        .catch((error) => {
            logger(
              `An error was encountered when requesting ${target}`,
              error
            );

            return null;
        });
};

export const bundleResponse: ProductDetailsBundleResponse = (
  response,
  logger,
  target
) => {
    if (response.status !== 200) {
      logger(
        `Status ${response.status} encountered when requesting ${target}`
      );

      return null;
    }

    return {
      requestName: target as searcherName,
      responseData: response.data as ProductDocumentsData | ProductRangesData,
    };
};
